import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ChevronUp,
  PlusCircle,
  RotateCcw,
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import { Filter } from "react-feather";
import Select from "react-select";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, GET_USER_STORES } from "../../Config";
import SpinLoader from "../loader/SpinLoader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const Estimates = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [billNo, setBillNo] = useState("");
  const [storesData, setStoresData] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [userDataSession, setUserDataSession] = useState(null);
 
  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserDataSession(userData); // You can store orgId/userId if needed for other calls
    } else {
      navigate("/");
    }
  }, [navigate]);
  const getStatusClassName = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "approved":
      case "paid":
        return "bg-success";

      case "pending":
      case "due":
      case "partial":
        return "bg-warning";

      case "cancelled":
      case "rejected":
        return "bg-danger";

      case "draft":
      case "open":
        return "bg-secondary";

      default:
        return "bg-secondary";
    }
  };
  const fetchStores = async () => {
    if (!userDataSession?.OrgId || !userDataSession?.UserId) return;

    try {
      const url = `${API_BASE_URL}${GET_USER_STORES}?OrgId=${userDataSession.OrgId}&UserId=${userDataSession.UserId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch stores");

      const data = await response.json();
      if (data?.ResultData) {
        setStoresData(data.ResultData);

        // Auto-select FIRST store
        if (data.ResultData.length > 0) {
          const firstStore = {
            value: data.ResultData[0].StoreId,
            label: data.ResultData[0].Name,
          };
          setSelectedStore(firstStore);
        }
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      MySwal.fire("Error", "Failed to load stores", "error");
    }
  };

  useEffect(() => {
    if (userDataSession?.OrgId && userDataSession?.UserId) {
      fetchStores();
    }
  }, [userDataSession]);

  // Store options for react-select
  const storeOptions = storesData.map((store) => ({
    value: store.StoreId,
    label: store.Name,
  }));

  const fetchEstimates = async () => {
    const userDataString = sessionStorage.getItem("userData");
    if (!userDataString) return;

    const userData = JSON.parse(userDataString);
    const orgId = userData.OrgId;
    const storeId = userData.StoreId;
    const userId = userData.UserId;

    if (!orgId || !storeId || !userId) return;

    setLoading(true);

    const params = new URLSearchParams();
    params.append("OrgId", orgId);
    params.append("StoreId", storeId);
    params.append("UserId", userId);

    if (billNo && billNo.trim() !== "") {
      params.append("BillNo", billNo.trim());
    }

    params.append("STdt", startDate || "2025-01-01");
    params.append("enddt", endDate || "2026-12-31");

    const url = `${API_BASE_URL}Billing/getEstimates?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.Status && Array.isArray(result.ResultData)) {
        setEstimates(result.ResultData);
      } else {
        setEstimates([]);
      }
    } catch (err) {
      console.error("Fetch estimates failed:", err);
      MySwal.fire("Error", "Failed to load estimates", "error");
      setEstimates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstimates();
  }, [startDate, endDate, billNo]);

  // Filter for client-side search
  const filteredEstimates = estimates.filter((record) => {
    if (!searchText.trim()) return true;

    const search = searchText.toLowerCase().trim();

    return (
      (record.BillNo || "").toLowerCase().includes(search) ||
      (record.Name || "").toLowerCase().includes(search) ||
      (record.Mobile || "").includes(search)
    );
  });

  // Export to PDF
  const exportToPDF = () => {
    if (filteredEstimates.length === 0) {
      MySwal.fire("No Data", "Nothing to export", "info");
      return;
    }

    const doc = new jsPDF("portrait", "pt", "a4");

    doc.setFontSize(16);
    doc.text("Estimates List", 40, 30);
    doc.setFontSize(10);
    doc.text(
      `Generated on: ${new Date().toLocaleString()} | Total: ${filteredEstimates.length}`,
      40,
      50,
    );

    const exportColumns = [
      "Bill No.",
      "Customer",
      "Mobile",
      "Date",
      "Type",
      "Items",
      "Advance",
      "Total",
      "Status",
    ];

    const exportData = filteredEstimates.map((record) => [
      record.BillNo || "-",
      record.Name || "Walk-in",
      record.Mobile || "-",
      record.QuotationDate
        ? new Date(record.QuotationDate).toLocaleDateString("en-GB")
        : "-",
      record.PaymentType || "-",
      record.ItemsCount || 0,
      `₹${parseFloat(record.Advance || 0).toFixed(2)}`,
      `₹${parseFloat(record.Total || 0).toFixed(2)}`,
      record.Status === 1 ? "Active" : "Inactive",
    ]);

    doc.autoTable({
      head: [exportColumns],
      body: exportData,
      startY: 65,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 65, left: 40, right: 40 },
    });

    doc.save(`Estimates_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredEstimates.length === 0) {
      MySwal.fire("No Data", "Nothing to export", "info");
      return;
    }

    const exportData = filteredEstimates.map((record) => ({
      "Bill No.": record.BillNo || "-",
      Customer: record.Name || "Walk-in",
      Mobile: record.Mobile || "-",
      "Quotation Date": record.QuotationDate
        ? new Date(record.QuotationDate).toLocaleDateString("en-GB")
        : "-",
      "Payment Type": record.PaymentType || "-",
      "Items Count": record.ItemsCount || 0,
      Advance: parseFloat(record.Advance || 0),
      Total: parseFloat(record.Total || 0),
      Status: record.Status === 1 ? "Active" : "Inactive",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Estimates");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Estimates_${new Date().toISOString().split("T")[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Refresh
  const handleRefresh = () => {
    fetchEstimates();
  };

  const toggleFilter = () => {
    setIsFilterVisible((prev) => !prev);
  };

  // Tooltip helpers (same as your AdvanceBooking)
  const renderPdfTooltip = (props) => <Tooltip {...props}>PDF</Tooltip>;
  const renderExcelTooltip = (props) => <Tooltip {...props}>Excel</Tooltip>;
  const renderRefreshTooltip = (props) => <Tooltip {...props}>Refresh</Tooltip>;
  const renderCollapseTooltip = (props) => (
    <Tooltip {...props}>Collapse</Tooltip>
  );

  const columns = [
    {
      title: "Bill No.",
      dataIndex: "BillNo",
      sorter: (a, b) => (a.BillNo || "").localeCompare(b.BillNo || ""),
    },
    {
      title: "Customer",
      dataIndex: "Name",
      render: (text) => text || "Walk-in",
      sorter: (a, b) => (a.Name || "").localeCompare(b.Name || ""),
    },
    {
      title: "Mobile",
      dataIndex: "Mobile",
    },
    {
      title: "Date",
      dataIndex: "QuotationDate",
      render: (text) =>
        text ? new Date(text).toLocaleDateString("en-GB") : "-",
      sorter: (a, b) => new Date(a.QuotationDate) - new Date(b.QuotationDate),
    },
    {
      title: "Type",
      dataIndex: "PaymentType",
    },
    {
      title: "Items",
      dataIndex: "ItemsCount",
    },
    {
      title: "Advance",
      dataIndex: "Advance",
      render: (text) => `₹${parseFloat(text || 0).toFixed(2)}`,
    },
    {
      title: "Total",
      dataIndex: "Total",
      render: (text) => `₹${parseFloat(text || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      render: (status, record) => {
        const isApproved = status === 1 && Number(record.DueAmount) === 0;

        // Map status to a value your helper understands
        // (adjust values if your getStatusClassName expects something else)
        const mappedStatus = isApproved ? "Completed" : "Pending";

        const className = getStatusClassName(mappedStatus);
        if (!className) return null;

        const textClass = className.replace("bg-", "text-");

        return (
          <i
            title={mappedStatus}
            className={`fa-solid fa-circle fs-4 ${textClass}`}
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
        render: () => (
          <div className="dropdown">
            <Link
              to="#"
              className="dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              <i className="fa-solid fa-ellipsis-vertical btn border" />
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link className="dropdown-item" to="#">
                <i className="fas fa-eye me-2" /> View
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="fas fa-edit me-2" /> Edit
              </Link>
              <Link className="dropdown-item text-danger" to="#">
                <i className="fas fa-gallery me-2" /> Image
              </Link>
            </div>
          </div>
        ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Estimates</h4>
              <h6>Manage Your Estimates</h6>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderPdfTooltip}>
                <Link onClick={exportToPDF}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="pdf" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                <Link onClick={exportToExcel}>
                  <ImageWithBasePath
                    src="assets/img/icons/excel.svg"
                    alt="excel"
                  />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link onClick={handleRefresh}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>

          <div className="page-btn">
            <Link to="/imageupload" className="btn btn-success">
              <PlusCircle className="me-2 d-none" />
              <i className="fa-solid fa-upload"></i>
            </Link>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search ..."
                  className="form-control form-control-sm formsearch"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <div className="search-path">
                <Link
                  className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                  onClick={toggleFilter}
                >
                  <Filter className="filter-icon" />
                  <span>
                    <ImageWithBasePath
                      src="assets/img/icons/closes.svg"
                      alt="close"
                    />
                  </span>
                </Link>
              </div>
            </div>

            {/* Filter Panel */}
            <div
              className={`card${isFilterVisible ? " visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row align-items-end">
                  <div className="col-lg-3 col-sm-6 col-12 d-none">
                    <div className="input-blocks">
                      <label className="form-label small">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 d-none">
                    <div className="input-blocks">
                      <label className="form-label small">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 d-none">
                    <div className="input-blocks">
                      <label className="form-label small">Bill No.</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 1542"
                        value={billNo}
                        onChange={(e) => setBillNo(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12 ">
                    <label className="form-label  mb-1">Store</label>
                    <Select
                      className="select"
                      options={storeOptions}
                      placeholder="Select Store"
                      value={selectedStore}
                      onChange={setSelectedStore}
                      isSearchable={true}
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="input-blocks">
                      <Link
                        className="btn btn-filters ms-auto"
                        onClick={fetchEstimates}
                      >
                        <i data-feather="search" className="feather-search" />{" "}
                        Search
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
              {loading ? (
                <SpinLoader />
              ) : (
                <Table columns={columns} dataSource={filteredEstimates} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Estimates;
